import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/shared/lib/api';
import {
  adminSupportApi,
  type AdminListTicketsParams,
  type TicketStatus,
} from '../api/admin-support-api';
import { notify } from '@/shared/lib/notify';

const ADMIN_TICKETS_KEY = ['admin', 'support', 'tickets'] as const;

export function useAdminTickets(params?: AdminListTicketsParams) {
  return useQuery({
    queryKey: [...ADMIN_TICKETS_KEY, params],
    queryFn: () => adminSupportApi.listTickets(params),
    staleTime: 15_000,
  });
}

export function useAdminTicket(id: string) {
  return useQuery({
    queryKey: ['admin', 'support', 'ticket', id],
    queryFn: () => adminSupportApi.getTicket(id),
    staleTime: 15_000,
    enabled: Boolean(id),
  });
}

export function useAdminTicketActivitySummary(ticketId: string, minutes: 15 | 60 | 1440) {
  return useQuery({
    queryKey: ['admin', 'support', 'ticket', ticketId, 'activity-summary', minutes],
    queryFn: () => adminSupportApi.getActivitySummary(ticketId, minutes),
    staleTime: 15_000,
    enabled: Boolean(ticketId),
  });
}

export function useAdminTicketUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'support', 'unread-count'],
    queryFn: () => adminSupportApi.getUnreadCount(),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useAdminTicketReply(ticketId: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (body: string) => adminSupportApi.postReply(ticketId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'support', 'ticket', ticketId] });
      void qc.invalidateQueries({ queryKey: ADMIN_TICKETS_KEY });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        notify.error(err);
      } else {
        notify.error(err, { title: t('errors.generic') });
      }
    },
  });
}

export function useAdminTicketStatus(ticketId: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (status: TicketStatus) => adminSupportApi.patchStatus(ticketId, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'support', 'ticket', ticketId] });
      void qc.invalidateQueries({ queryKey: ADMIN_TICKETS_KEY });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        notify.error(err);
      } else {
        notify.error(err, { title: t('errors.generic') });
      }
    },
  });
}
